import { CSSResultGroup, TemplateResult, html } from 'lit';
import { OutlineElement } from '../../base/outline-element/outline-element';
import { customElement, property } from 'lit/decorators.js';
import componentStyles from './tufts-pulse.css.lit';
enum pulseOrientation {
  'top-right',
  'bottom-right',
  'bottom-left',
  'top-left',
}
/**
 * The TuftsPulse component creates a decorative pulse effect with concentric circles.
 * The pulse can be positioned in different orientations and uses a color scheme
 * that can be customized to match the container's theme.
 *
 * @element tufts-pulse
 *
 * @attr orientation - Position of the pulse (top-right, bottom-right, bottom-left, top-left)
 * @attr max-size - Maximum size of the pulse effect
 * @attr pulse-top-alignment - Aligns the pulse to the top of the container
 * @attr pulse-color - Color scheme for the pulse effect (e.g., 'blue', 'green')
 */
@customElement('tufts-pulse')
export class TuftsPulse extends OutlineElement {
  static styles: CSSResultGroup = [componentStyles];
  /**
   * The orientation of the pulse.
   **/
  @property({
    type: String,
    reflect: true,
    attribute: 'orientation',
    converter: orientation => {
      if (orientation && !(orientation in pulseOrientation)) {
        orientation = 'top-right';
      }
      return orientation;
    },
  })
  orientation: pulseOrientation;

  /**
   * Optional max-size for the pulse
   **/
  @property({
    type: String,
    reflect: true,
    attribute: 'max-size',
  })
  maxSize = '650px';

  @property({
    type: String,
    reflect: true,
    attribute: 'pulse-top-alignment',
  })
  pulseTopAlignment = false;

  @property({
    type: String,
    reflect: true,
    attribute: 'pulse-color',
  })
  color = 'blue';

  render(): TemplateResult {
    return html`
      ${this.maxSize
        ? html` <style>
            .pulse {
              --max-size: ${this.maxSize};
            }
          </style>`
        : null}
      ${this.pulseSVG()}
    `;
  }

  /**
   * pulseSVG template
   *
   * @returns - The pulse symbol.
   */
  pulseSVG(): TemplateResult {
    return html`
      <svg
        class="pulse ${this.orientation} ${this.pulseTopAlignment}"
        xmlns="http://www.w3.org/2000/svg"
        fill="none"
        viewBox="0 0 100 100"
      >
        <circle
          class="arc-4"
          cx="100%"
          cy="0%"
          r="90%"
          fill="var(--dark-${this.color})"
        />
        <circle
          class="arc-3"
          cx="100%"
          cy="0%"
          r="67.5%"
          fill="var(--medium-${this.color})"
        />
        <circle
          class="arc-2"
          cx="100%"
          cy="0%"
          r="45%"
          fill="var(--core-${this.color})"
        />
        <circle
          class="arc-1"
          cx="100%"
          cy="0%"
          r="22.5%"
          fill="var(--light-${this.color})"
        />
      </svg>
    `;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'tufts-pulse': TuftsPulse;
  }
}
